/* ============================================================
   Akshaya Organics — Premium Motion Layer (JS)
   - Scroll reveals with stagger (IntersectionObserver)
   - Hero entrance + Ken Burns
   - Momentum (lerp) wheel scrolling, desktop only
   - Glass header: hide on scroll down, reveal on scroll up
   - Gentle parallax on banner media
   - Apple-style glass scroll-out of sections (homepage only)
   - Blur-up image loading
   - Cart bubble pulse
   - Hide empty product accordion tabs (Benefits/Ingredients/How to Use)
   Fully gated: no-ops in Shopify design mode, with reduced
   motion, or without IntersectionObserver support.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- 0. Hide empty product accordion tabs ---------- */
  if (!(window.Shopify && Shopify.designMode)) {
    var hideEmptyAccordions = function () {
      document.querySelectorAll('.product__accordion').forEach(function (acc) {
        var content = acc.querySelector('.accordion__content');
        if (content && content.textContent.replace(/\s+/g, '') === '') {
          acc.style.display = 'none';
        }
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hideEmptyAccordions, { once: true });
    } else {
      hideEmptyAccordions();
    }
  }

  if (window.Shopify && Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) return;

  var docEl = document.documentElement;
  docEl.classList.add('ak-motion');

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    var heroSection = document.querySelector('#MainContent .shopify-section');

    /* ---------- 1. Scroll reveals ---------- */
    var revealSelectors = [
      '.product-card-wrapper',
      '.collection-list__item',
      '.multicolumn-list__item',
      '.article-card-wrapper',
      '.title-wrapper-with-link',
      '.rich-text__blocks',
      '.image-with-text__grid',
      '.newsletter__wrapper',
      '.footer-block',
      // Akshaya custom sections
      '.wc-head', '.wc-item',                        // Why Choose Akshaya
      '.pt-head', '.pt-stat', '.pt-card',            // Premium testimonials
      '.hsb-story-copy', '.hsb-info-card', '.hsb-illustration', // Heritage story
      '.nls-content', '.nls-media'                   // New launch spotlight
    ];
    var revealEls = [];
    revealSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        // skip things Dawn's own scroll-trigger already animates
        if (el.closest('.scroll-trigger')) return;
        if (el.classList.contains('scroll-trigger')) return;
        // never touch the first (hero) section — it has its own entrance
        if (heroSection && heroSection.contains(el)) return;
        revealEls.push(el);
      });
    });

    // stagger siblings that share a parent
    var groups = new Map();
    revealEls.forEach(function (el) {
      var p = el.parentElement;
      if (!groups.has(p)) groups.set(p, 0);
      var i = groups.get(p);
      el.style.setProperty('--ak-d', Math.min(i * 90, 450) + 'ms');
      groups.set(p, i + 1);
      el.classList.add('ak-reveal');
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('ak-in');
            io.unobserve(entry.target);
            setTimeout(function () {
              entry.target.classList.add('ak-done');
            }, 1500);
          }
        });
      },
      { rootMargin: '0px 0px 15% 0px', threshold: 0.01 }
    );
    revealEls.forEach(function (el) { io.observe(el); });

    /* ---------- 3. Hero entrance + Ken Burns ---------- */
    if (heroSection) {
      var heroBox = heroSection.querySelector(
        '.banner__box, .banner__content, .swiper-slide-active [class*="custom-slideshow-content"]'
      );
      if (heroBox) {
        var kids = heroBox.querySelectorAll('.banner__box > *');
        if (!kids.length) kids = heroBox.children;
        Array.prototype.forEach.call(kids, function (el, i) {
          el.style.setProperty('--ak-d', 150 + i * 140 + 'ms');
          el.classList.add('ak-hero-item');
        });
      }
      var heroImg = heroSection.querySelector(
        '.banner__media img, .swiper-slide-active img'
      );
      if (heroImg) heroImg.classList.add('ak-kenburns');
    }

    /* ---------- 4. Momentum wheel scrolling (desktop, fine pointer) ---------- */
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (finePointer) {
      var target = window.scrollY;
      var current = window.scrollY;
      var rafId = null;
      var maxScroll = function () {
        return Math.max(0, docEl.scrollHeight - window.innerHeight);
      };
      var loop = function () {
        current += (target - current) * 0.11;
        if (Math.abs(target - current) < 0.5) {
          current = target;
          rafId = null;
        } else {
          rafId = requestAnimationFrame(loop);
        }
        window.scrollTo({ top: current, behavior: 'instant' });
      };
      window.addEventListener(
        'wheel',
        function (e) {
          // let pinch-zoom, modified scrolls & scrollable inner panels behave natively
          if (e.ctrlKey || e.defaultPrevented) return;
          var node = e.target;
          while (node && node !== document.body) {
            if (node.scrollHeight > node.clientHeight + 2) {
              var oy = getComputedStyle(node).overflowY;
              if (oy === 'auto' || oy === 'scroll') return;
            }
            node = node.parentElement;
          }
          e.preventDefault();
          var delta = e.deltaY;
          if (e.deltaMode === 1) delta *= 40; // lines -> px
          target = Math.max(0, Math.min(maxScroll(), target + delta));
          if (rafId === null) rafId = requestAnimationFrame(loop);
        },
        { passive: false }
      );
      // re-sync when the user scrolls by other means (keyboard, drag, anchor)
      window.addEventListener('scroll', function () {
        if (rafId === null) {
          target = window.scrollY;
          current = window.scrollY;
        }
      }, { passive: true });
    }

    /* ---------- 6. Glass header: hide/reveal + scrolled state ---------- */
    var lastY = window.scrollY;
    var ticking = false;
    function onScrollDir() {
      var y = window.scrollY;
      docEl.classList.toggle('ak-scrolled', y > 60);
      if (y > 260 && y > lastY + 4) {
        docEl.classList.add('ak-header-hidden');
      } else if (y < lastY - 4 || y <= 260) {
        docEl.classList.remove('ak-header-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScrollDir);
      }
    }, { passive: true });
    onScrollDir();

    /* ---------- 7. Gentle parallax on banner / image-with-text media ---------- */
    if (finePointer) {
      var pEls = [];
      document.querySelectorAll(
        '.image-with-text__media img, .banner__media img'
      ).forEach(function (img) {
        if (img.classList.contains('ak-kenburns')) return; // hero has Ken Burns
        pEls.push({ img: img, visible: false });
        img.classList.add('ak-parallax');
      });
      if (pEls.length) {
        var pio = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var rec = pEls.find(function (r) { return r.img === entry.target; });
            if (rec) rec.visible = entry.isIntersecting;
          });
        }, { rootMargin: '20% 0px 20% 0px' });
        pEls.forEach(function (r) { pio.observe(r.img); });

        var pTicking = false;
        var updateParallax = function () {
          pEls.forEach(function (r) {
            if (!r.visible) return;
            var rect = r.img.getBoundingClientRect();
            var vh = window.innerHeight;
            // progress: -1 (below viewport) .. 1 (above viewport)
            var progress = ((rect.top + rect.height / 2) - vh / 2) / (vh / 2 + rect.height / 2);
            var shift = Math.max(-1, Math.min(1, progress)) * -5; // max 5%
            r.img.style.setProperty('--ak-py', shift.toFixed(2) + '%');
          });
          pTicking = false;
        };
        window.addEventListener('scroll', function () {
          if (!pTicking) {
            pTicking = true;
            requestAnimationFrame(updateParallax);
          }
        }, { passive: true });
        updateParallax();
      }
    }

    /* ---------- 9. Apple glass scroll-out (HOMEPAGE only, desktop) ----------
       On collection/product pages the main content is one tall section, so
       fading it out would hide products while the user is still shopping. */
    var mainEl = document.querySelector('main[data-template]');
    var isHomepage = mainEl && mainEl.getAttribute('data-template') === 'index';
    if (finePointer && isHomepage) {
      var gSections = Array.prototype.slice.call(
        document.querySelectorAll('#MainContent .shopify-section')
      );
      var gTicking = false;
      var updateGlassOut = function () {
        var vh = window.innerHeight;
        gSections.forEach(function (s) {
          var rect = s.getBoundingClientRect();
          if (rect.height < 50) return; // skip empty sections
          if (rect.height > vh * 2.2) return; // skip very tall sections — user may still be reading them
          if (rect.top < 0 && rect.bottom > -vh) {
            // section is scrolling out through the top of the viewport
            var p = Math.min(1, -rect.top / (rect.height * 0.75));
            var eased = p * p; // ease-in: subtle at first, stronger near exit
            s.classList.add('ak-glass-out');
            s.style.setProperty('--ak-go', (1 - 0.75 * eased).toFixed(3));
            s.style.setProperty('--ak-gb', (9 * eased).toFixed(2) + 'px');
            s.style.setProperty('--ak-gs', (1 - 0.035 * eased).toFixed(4));
          } else if (s.classList.contains('ak-glass-out')) {
            s.classList.remove('ak-glass-out');
            s.style.removeProperty('--ak-go');
            s.style.removeProperty('--ak-gb');
            s.style.removeProperty('--ak-gs');
          }
        });
        gTicking = false;
      };
      window.addEventListener('scroll', function () {
        if (!gTicking) {
          gTicking = true;
          requestAnimationFrame(updateGlassOut);
        }
      }, { passive: true });
      updateGlassOut();
    }

    /* ---------- 8. Blur-up image loading ---------- */
    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return; // already loaded, leave alone
      img.classList.add('ak-img');
      var done = function () { img.classList.add('ak-loaded'); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true }); // never leave hidden
      // safety net: reveal after 4s no matter what
      setTimeout(done, 4000);
    });

    /* ---------- 5. Cart bubble pulse on count change ---------- */
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        var bubble = cartIcon.querySelector('.cart-count-bubble');
        if (!bubble) return;
        bubble.classList.remove('ak-pulse');
        void bubble.offsetWidth; // restart animation
        bubble.classList.add('ak-pulse');
      });
      mo.observe(cartIcon, { childList: true, subtree: true, characterData: true });
    }
  });
})();
